/**
 * Parrot fluency-level data — pure data + pure functions, no UI.
 *
 * Faithful web port of the iOS source of truth:
 *   - client-native/src/utils/progress/levels.ts            (LEVELS curve, hoursToLevel/progressToNext)
 *   - client-native/src/features/home/components/parrot-levels/levelData.ts  (names/emoji, SHEET_LEVELS)
 *   - client-native/src/utils/progress/levelIcons.ts        (LEVEL_ICONS asset map)
 *   - client-native/src/features/activation/activationSteps.ts (threshold constants)
 *
 * Names, emoji, hour thresholds, CEFR labels and goal values are kept EXACT.
 * Other tracks (home-levels, profile, courses, placement) consume this module.
 */

// ---------------------------------------------------------------------------
// Activation thresholds (source: client-native/src/features/activation/activationSteps.ts)
// ---------------------------------------------------------------------------

export const WORDS_LEVEL_UP_THRESHOLD = 22;
export const REVIEW_LEVEL_UP_THRESHOLD = 22;
export const PHRASES_LEVEL_UP_THRESHOLD = 11;

/** Number of activation-phase level-up goals (ACTIVATION_LEVEL_GOALS.length on iOS). */
export const ACTIVATION_LEVEL_COUNT = 9;

// ---------------------------------------------------------------------------
// Level curve (source: client-native/src/utils/progress/levels.ts)
// ---------------------------------------------------------------------------

export const TOTAL_LEVELS = 50;
export const TOTAL_HOURS = 1500;
const HOURS_PER_DAY = 0.25; // 15 min/day

export interface Level {
  id: number;
  hours: number;
  cefr: string;
  words: number;
  deltaHours: number;
  etaDaysAt15min: number;
}

/** Complete 50-level progression data: cumulative hours, CEFR equivalents, known words. */
const LEVEL_DATA: Omit<Level, "deltaHours" | "etaDaysAt15min">[] = [
  { id: 1, hours: 0, cefr: "A0", words: 0 },
  { id: 2, hours: 1 / 60, cefr: "A0", words: 50 },
  { id: 3, hours: 2 / 60, cefr: "A0", words: 100 },
  { id: 4, hours: 3 / 60, cefr: "A0", words: 150 },
  { id: 5, hours: 4 / 60, cefr: "A0", words: 200 },
  { id: 6, hours: 5 / 60, cefr: "A1", words: 300 },
  { id: 7, hours: 0.25, cefr: "A1", words: 400 },
  { id: 8, hours: 0.5, cefr: "A1", words: 550 },
  { id: 9, hours: 0.75, cefr: "A1", words: 700 },
  { id: 10, hours: 1, cefr: "A1", words: 850 },
  { id: 11, hours: 1.5, cefr: "A1", words: 1000 },
  { id: 12, hours: 2, cefr: "A1", words: 1150 },
  { id: 13, hours: 4, cefr: "A2", words: 1300 },
  { id: 14, hours: 6, cefr: "A2", words: 1450 },
  { id: 15, hours: 8, cefr: "A2", words: 1600 },
  { id: 16, hours: 12, cefr: "A2", words: 1750 },
  { id: 17, hours: 17, cefr: "A2", words: 1900 },
  { id: 18, hours: 24, cefr: "A2", words: 2000 },
  { id: 19, hours: 32, cefr: "A2", words: 2250 },
  { id: 20, hours: 42, cefr: "A2", words: 2500 },
  { id: 21, hours: 54, cefr: "B1", words: 2750 },
  { id: 22, hours: 68, cefr: "B1", words: 3000 },
  { id: 23, hours: 84, cefr: "B1", words: 3250 },
  { id: 24, hours: 102, cefr: "B1", words: 3500 },
  { id: 25, hours: 122, cefr: "B1", words: 3750 },
  { id: 26, hours: 145, cefr: "B1", words: 4000 },
  { id: 27, hours: 170, cefr: "B1", words: 4250 },
  { id: 28, hours: 198, cefr: "B1", words: 4500 },
  { id: 29, hours: 228, cefr: "B1", words: 4750 },
  { id: 30, hours: 260, cefr: "B1", words: 5000 },
  { id: 31, hours: 295, cefr: "B2", words: 5250 },
  { id: 32, hours: 333, cefr: "B2", words: 5500 },
  { id: 33, hours: 374, cefr: "B2", words: 5750 },
  { id: 34, hours: 418, cefr: "B2", words: 6000 },
  { id: 35, hours: 465, cefr: "B2", words: 6250 },
  { id: 36, hours: 515, cefr: "B2", words: 6500 },
  { id: 37, hours: 568, cefr: "B2", words: 6750 },
  { id: 38, hours: 624, cefr: "B2", words: 7000 },
  { id: 39, hours: 683, cefr: "B2", words: 7250 },
  { id: 40, hours: 745, cefr: "B2", words: 7500 },
  { id: 41, hours: 810, cefr: "C1", words: 8000 },
  { id: 42, hours: 875, cefr: "C1", words: 8500 },
  { id: 43, hours: 940, cefr: "C1", words: 9000 },
  { id: 44, hours: 1007, cefr: "C1", words: 9500 },
  { id: 45, hours: 1075, cefr: "C1", words: 10000 },
  { id: 46, hours: 1145, cefr: "C1", words: 10500 },
  { id: 47, hours: 1216, cefr: "C2", words: 11000 },
  { id: 48, hours: 1288, cefr: "C2", words: 12000 },
  { id: 49, hours: 1390, cefr: "C2", words: 13500 },
  { id: 50, hours: 1500, cefr: "C2", words: 15000 },
];

/** Exported levels with computed delta hours and ETA. */
export const LEVELS: Level[] = LEVEL_DATA.map((level, index) => {
  const prevHours = index === 0 ? 0 : LEVEL_DATA[index - 1].hours;
  const deltaHours = level.hours - prevHours;
  const etaDaysAt15min = Math.ceil(deltaHours / HOURS_PER_DAY);
  return { ...level, deltaHours, etaDaysAt15min };
});

/**
 * Given total listened hours, return the current level id (1..50).
 * If hours >= 1500, returns 50 (max level).
 */
export function hoursToLevel(totalHours: number): number {
  if (totalHours < 0) return 1;
  if (totalHours >= TOTAL_HOURS) return TOTAL_LEVELS;

  // Binary search for efficiency.
  let left = 0;
  let right = LEVELS.length - 1;

  while (left < right) {
    const mid = Math.floor((left + right + 1) / 2);
    if (LEVELS[mid].hours <= totalHours) {
      left = mid;
    } else {
      right = mid - 1;
    }
  }

  return LEVELS[left].id;
}

// ---------------------------------------------------------------------------
// Level names + emoji (source: levelData.ts → LEVEL_NAMES)
// ---------------------------------------------------------------------------

const LEVEL_NAMES: { name: string; emoji: string }[] = [
  { name: "Egg Crack", emoji: "🥚" },
  { name: "First Chirp", emoji: "🐣" },
  { name: "Tiny Feather", emoji: "🪶" },
  { name: "Nest Builder", emoji: "🪹" },
  { name: "Seed Finder", emoji: "🌱" },
  { name: "Dawn Chirper", emoji: "🌅" },
  { name: "Branch Hopper", emoji: "🌿" },
  { name: "Canopy Scout", emoji: "🌳" },
  { name: "Breeze Rider", emoji: "🍃" },
  { name: "Fledgling", emoji: "🐥" },
  { name: "Treetop Dweller", emoji: "🏡" },
  { name: "Berry Picker", emoji: "🫐" },
  { name: "Sunrise Watcher", emoji: "🌄" },
  { name: "Cloud Chaser", emoji: "☁️" },
  { name: "Valley Explorer", emoji: "🏞️" },
  { name: "River Crosser", emoji: "🏊" },
  { name: "Mountain Gazer", emoji: "⛰️" },
  { name: "Storm Braver", emoji: "⛈️" },
  { name: "Jungle Navigator", emoji: "🌴" },
  { name: "Horizon Seeker", emoji: "🔭" },
  { name: "Coral Spotter", emoji: "🪸" },
  { name: "Tide Reader", emoji: "🌊" },
  { name: "Island Hopper", emoji: "🏝️" },
  { name: "Starlight Flyer", emoji: "✨" },
  { name: "Voyager", emoji: "🚀" },
  { name: "Desert Crosser", emoji: "🏜️" },
  { name: "Oasis Finder", emoji: "💧" },
  { name: "Canyon Echo", emoji: "🗣️" },
  { name: "Aurora Chaser", emoji: "🌌" },
  { name: "Peak Climber", emoji: "🧗" },
  { name: "Glacier Glider", emoji: "🧊" },
  { name: "Thunder Wing", emoji: "⚡" },
  { name: "Moonlit Sage", emoji: "🌙" },
  { name: "Trailblazer", emoji: "🔥" },
  { name: "World Walker", emoji: "🌍" },
  { name: "Dialect Dancer", emoji: "💃" },
  { name: "Story Weaver", emoji: "📖" },
  { name: "Phrase Alchemist", emoji: "⚗️" },
  { name: "Silver Tongue", emoji: "🪙" },
  { name: "Polyglot Rising", emoji: "🗺️" },
  { name: "Rhythm Speaker", emoji: "🎶" },
  { name: "Golden Ear", emoji: "👂" },
  { name: "Sunset Sage", emoji: "🌇" },
  { name: "Dream Weaver", emoji: "🎭" },
  { name: "Parrot Elder", emoji: "🦉" },
  { name: "Lingua Guardian", emoji: "🛡️" },
  { name: "Phoenix Speaker", emoji: "🔶" },
  { name: "Constellation", emoji: "⭐" },
  { name: "Legendary Parrot", emoji: "👑" },
  { name: "Fluent Forever", emoji: "🏆" },
];

export interface ParrotLevel {
  level: number;
  name: string;
  hoursRequired: number;
  emoji: string;
}

/**
 * 50 Parrot Fluency Levels mapped from the canonical level curve.
 * Used by LevelUp UI and the global store.
 */
export const PARROT_LEVELS: ParrotLevel[] = LEVELS.map((lvl, i) => ({
  level: lvl.id,
  name: LEVEL_NAMES[i]?.name ?? `Level ${lvl.id}`,
  hoursRequired: lvl.hours,
  emoji: LEVEL_NAMES[i]?.emoji ?? "🦜",
}));

export const getCurrentLevel = (totalSeconds: number): ParrotLevel => {
  const hours = totalSeconds / 3600;
  let current = PARROT_LEVELS[0];
  for (const lvl of PARROT_LEVELS) {
    if (hours >= lvl.hoursRequired) current = lvl;
    else break;
  }
  return current;
};

export const getNextLevel = (totalSeconds: number): ParrotLevel | null => {
  const hours = totalSeconds / 3600;
  for (const lvl of PARROT_LEVELS) {
    if (hours < lvl.hoursRequired) return lvl;
  }
  return null;
};

// ---------------------------------------------------------------------------
// SHEET_LEVELS — per-row goal data for the levels sheet (source: levelData.ts)
// ---------------------------------------------------------------------------

export type ProgressCheckType =
  | "totalWords"
  | "learningWords"
  | "reviewedCards"
  | "knownWords"
  | "completedLessons"
  | "phrases"
  | "listeningTime";

export interface SheetLevel {
  level: number;
  name: string;
  emoji: string;
  requirement: string;
  checkType: ProgressCheckType;
  goalValue: number;
  prevGoalValue: number;
}

const ACTIVATION_LEVELS: SheetLevel[] = [
  {
    level: 1,
    name: "Egg Crack",
    emoji: "🥚",
    requirement: `Save ${WORDS_LEVEL_UP_THRESHOLD} words`,
    checkType: "learningWords",
    goalValue: WORDS_LEVEL_UP_THRESHOLD,
    prevGoalValue: 0,
  },
  {
    level: 2,
    name: "First Chirp",
    emoji: "🐣",
    requirement: `Review ${REVIEW_LEVEL_UP_THRESHOLD} flash cards`,
    checkType: "reviewedCards",
    goalValue: REVIEW_LEVEL_UP_THRESHOLD,
    prevGoalValue: 0,
  },
  {
    level: 3,
    name: "Tiny Feather",
    emoji: "🪶",
    requirement: "Master 21 words",
    checkType: "knownWords",
    goalValue: 21,
    prevGoalValue: 0,
  },
  {
    level: 4,
    name: "Nest Builder",
    emoji: "🪹",
    requirement: "Complete 8 lessons",
    checkType: "completedLessons",
    goalValue: 8,
    prevGoalValue: 0,
  },
  {
    level: 5,
    name: "Seed Finder",
    emoji: "🌱",
    requirement: `Highlight ${PHRASES_LEVEL_UP_THRESHOLD} phrases`,
    checkType: "phrases",
    goalValue: PHRASES_LEVEL_UP_THRESHOLD,
    prevGoalValue: 0,
  },
  {
    level: 6,
    name: "Dawn Chirper",
    emoji: "🌅",
    requirement: "15 min listening",
    checkType: "listeningTime",
    goalValue: 900,
    prevGoalValue: 0,
  },
  {
    level: 7,
    name: "Branch Hopper",
    emoji: "🌿",
    requirement: "30 min listening",
    checkType: "listeningTime",
    goalValue: 1800,
    prevGoalValue: 900,
  },
  {
    level: 8,
    name: "Canopy Scout",
    emoji: "🌳",
    requirement: "45 min listening",
    checkType: "listeningTime",
    goalValue: 2700,
    prevGoalValue: 1800,
  },
  {
    level: 9,
    name: "Breeze Rider",
    emoji: "🍃",
    requirement: "60 min listening",
    checkType: "listeningTime",
    goalValue: 3600,
    prevGoalValue: 2700,
  },
  {
    level: 10,
    name: "Fledgling",
    emoji: "🐥",
    requirement: "90 min listening",
    checkType: "listeningTime",
    goalValue: 5400,
    prevGoalValue: 3600,
  },
];

const POST_ACTIVATION_LEVELS: SheetLevel[] = (() => {
  const postLevels: SheetLevel[] = [];
  const postThresholds: { level: number; hours: number }[] = LEVELS.filter(
    (l) => l.id > ACTIVATION_LEVEL_COUNT + 1,
  ).map((l) => ({ level: l.id, hours: l.hours }));

  for (let i = 0; i < postThresholds.length; i++) {
    const t = postThresholds[i];
    if (t.level > 50) break;
    const nameIdx = t.level - 1;
    const nextHours = postThresholds[i + 1]?.hours;
    postLevels.push({
      level: t.level,
      name: LEVEL_NAMES[nameIdx]?.name ?? `Level ${t.level}`,
      emoji: LEVEL_NAMES[nameIdx]?.emoji ?? "🦜",
      requirement: nextHours ? `${nextHours}h listening` : "Max level!",
      checkType: "listeningTime",
      goalValue: nextHours ? nextHours * 3600 : t.hours * 3600,
      prevGoalValue: t.hours * 3600,
    });
  }
  return postLevels;
})();

export const SHEET_LEVELS: SheetLevel[] = [
  ...ACTIVATION_LEVELS,
  ...POST_ACTIVATION_LEVELS,
];

